import { useState, useEffect, useRef, useCallback } from 'react'
import { listSettings } from '../../utils/settingsRegistry'
import { runActions, listPanels, chartContext, listGrokActions } from '../../utils/grokActions'

// ============================================================
// useLobbyChat — open lobby chat over the main data WS.
// ------------------------------------------------------------
// Pre-auth: claims a display name via chat_join (server validates
// format + uniqueness among live connections). Name persists in
// localStorage and auto-rejoins. A display name from GET /api/display-name
// locks joined (cheddarChatJoined) so a reload or a dropped socket skips
// the name field and re-sends chat_join with that saved name. When auth lands, the session
// token replaces the name claim server-side — this hook's message
// flow doesn't change.
//
// Protocol (server_swaps.py):
//   send: { action: 'chat_join', name }
//         { action: 'chat_send', text }
//         { action: 'grok_ask', text, context }  // private, this socket only
//         { action: 'chat_history' }
//         { action: 'chat_react', id, emoji }    // toggle your reaction
//   recv: { type: 'chat_joined', name }
//         { type: 'chat_msg', id, name, text, ts }   // ts in ms
//         { type: 'chat_history', messages: [...] }
//         { type: 'chat_react', id, reactions }  // {emoji: [names]}
//         { type: 'chat_error', reason }
//
// Params:
//   wsRef    — ref to the main data WebSocket (data.wsRef)
//   status   — data.status; real state that flips when the WS opens.
//              Refs don't trigger re-renders, so depending on
//              wsRef?.current alone would attach the listener to a
//              null ws indefinitely (same trap as the HL wallet
//              wiring — see that comment in FootprintChartCanvas).
//   active   — gates history fetch, auto-rejoin, and scroll pinning.
//              Now pinned true by App so the always-mounted docked
//              panel keeps live chat; it NO LONGER gates unread.
//   onUnread — called once per incoming chat_msg; the caller bumps
//              its own unread badge and owns suppression (it clears
//              the count when the chat view is actually being read).
// ============================================================

const NAME_STORAGE_KEY = 'cheddarChatName'
// Stays set once the email display name (or a confirmed join) is known.
// An admin reset can clear it later. This hook never clears it.
const JOINED_STORAGE_KEY = 'cheddarChatJoined'

// Chat key, sent with chat_join so the server can tell YOUR other
// connections apart from someone else (those used to be rejected as "name
// taken", dropping you back to the name prompt). Logged in: the ACCOUNT key
// from /api/display-name — same on phone, desktop and every window.
// Logged out: a per-browser key in localStorage.
const BROWSER_KEY = (() => {
  try {
    let k = localStorage.getItem('cheddarChatKey')
    if (!k) {
      k = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`)
      localStorage.setItem('cheddarChatKey', k)
    }
    return k
  } catch { return '' }
})()
let chatKey = BROWSER_KEY
const adoptKey = (k) => { if (typeof k === 'string' && k.length >= 8) chatKey = k }
const joinMsg = (name) => JSON.stringify({ action: 'chat_join', name, key: chatKey })
const MAX_MESSAGES = 200

// Toggle `who` in one emoji's name list on a message. Returns a new message.
const toggleReaction = (m, emoji, who) => {
  const reactions = { ...(m.reactions || {}) }
  const names = Array.isArray(reactions[emoji]) ? reactions[emoji] : []
  const next = names.includes(who) ? names.filter(n => n !== who) : [...names, who]
  if (next.length) reactions[emoji] = next
  else delete reactions[emoji]
  return { ...m, reactions }
}

export default function useLobbyChat({ wsRef, status, active, onUnread, getChartContextRef }) {
  const [messages, setMessages] = useState([])   // [{id, name, text, ts}]
  const [name, setName] = useState(() => {
    try { return localStorage.getItem(NAME_STORAGE_KEY) || '' } catch { return '' }
  })
  // Saved display name + flag: start joined so refresh does not ask for a name.
  const [joined, setJoined] = useState(() => {
    try {
      return localStorage.getItem(JOINED_STORAGE_KEY) === '1' && !!localStorage.getItem(NAME_STORAGE_KEY)
    } catch { return false }
  })
  // Hide the name field until GET /api/display-name settles, unless already joined.
  const [identityReady, setIdentityReady] = useState(() => {
    try {
      return localStorage.getItem(JOINED_STORAGE_KEY) === '1' && !!localStorage.getItem(NAME_STORAGE_KEY)
    } catch { return false }
  })
  const [input, setInput] = useState('')
  const [error, setError] = useState('')
  const bodyRef = useRef(null)                   // attach to the scrollable list
  const historyRequestedRef = useRef(false)

  // Mirrors for the WS handler (stable closure) to read current joined/name
  // and the latest unread callback without re-subscribing the listener.
  const nameRef = useRef(name)
  const rejoinAtRef = useRef(0)
  // True only after THIS socket has been accepted. A duplicate chat_join
  // after that is our own name, not someone else taking it.
  const ackedRef = useRef(false)
  const onUnreadRef = useRef(onUnread)
  useEffect(() => { onUnreadRef.current = onUnread }, [onUnread])
  useEffect(() => { nameRef.current = name }, [name])

  // Listen on the main data WS — same attach pattern as the
  // boost_new listener in FootprintChartCanvas.
  useEffect(() => {
    const ws = wsRef?.current
    if (!ws) return
    const handler = (e) => {
      let msg
      try { msg = JSON.parse(e.data) } catch { return }
      if (!msg || !msg.type) return
      if (msg.type === 'chat_msg') {
        setMessages(prev => {
          const next = [...prev, msg]
          return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next
        })
        // Bump unread for EVERY incoming chat_msg. `active` is now pinned
        // true by App (the docked panel needs live chat permanently), so the
        // old `!active` gate suppressed onUnread forever — the green footer
        // badge could never appear. Suppression is the caller's job: the
        // footer popup's clear effect zeroes the count while it's open on the
        // Chat tab, mirroring how the red log badge works.
        if (onUnreadRef.current) onUnreadRef.current()
      } else if (msg.type === 'chat_react') {
        // Server's full reaction map for one message replaces ours.
        const reactions = (msg.reactions && typeof msg.reactions === 'object') ? msg.reactions : {}
        setMessages(prev => prev.map(m => (m.id === msg.id ? { ...m, reactions } : m)))
      } else if (msg.type === 'chat_history') {
        const pub = Array.isArray(msg.messages) ? msg.messages : []
        setMessages(prev => {
          const priv = prev.filter(m => m.private)
          if (!priv.length) return pub
          const merged = [...pub, ...priv].sort((a, b) => (a.ts || 0) - (b.ts || 0))
          return merged.length > MAX_MESSAGES ? merged.slice(-MAX_MESSAGES) : merged
        })
      } else if (msg.type === 'grok_actions') {
        // Settings / tour actions from Grok. The runner validates every one
        // against the registry (blocklist, types, ranges, confirm) itself.
        const acts = Array.isArray(msg.actions) ? msg.actions : []
        runActions(acts).then(results => {
          const w = wsRef?.current
          if (w && w.readyState === WebSocket.OPEN) {
            try { w.send(JSON.stringify({ action: 'grok_action_result', id: msg.id, results })) } catch {}
          }
          const bad = results.filter(r => r && !r.ok && r.error && r.error !== 'tour closed')
          if (bad.length) {
            const ts = Date.now()
            setMessages(prev => [...prev, { id: `ga_${ts}`, name: 'Grok', private: true, ts,
              text: bad.map(r => `${r.id || r.menu || r.panel || r.op}: ${r.error}`).join('; ') }].slice(-MAX_MESSAGES))
          }
        })
      } else if (msg.type === 'grok_msg') {
        // Private chart reply. This socket only; never a lobby broadcast.
        setMessages(prev => {
          const next = [...prev, { ...msg, private: true, name: msg.name || 'Grok' }]
          return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next
        })
        if (onUnreadRef.current) onUnreadRef.current()
      } else if (msg.type === 'chat_joined') {
        const n = (msg.name || nameRef.current || '').trim()
        if (n) {
          try { localStorage.setItem(NAME_STORAGE_KEY, n) } catch {}
          nameRef.current = n
          setName(n)
        }
        try { localStorage.setItem(JOINED_STORAGE_KEY, '1') } catch {}
        ackedRef.current = true
        setJoined(true)
        setError('')
      } else if (msg.type === 'chat_error') {
        // "name taken" while we're ALREADY joined under that same name is a
        // self-collision (a duplicate chat_join for our own live name —
        // e.g. auto-join + click). Not a real error: we hold the name.
        const takenReasons = ['name taken', 'name already taken', 'name in use']
        const isSelfTaken =
          ackedRef.current &&
          takenReasons.includes((msg.reason || '').toLowerCase()) &&
          nameRef.current
        if (isSelfTaken) {
          try { localStorage.setItem(JOINED_STORAGE_KEY, '1') } catch {}
          setJoined(true)
          setError('')
        } else {
          setError(msg.reason || 'chat error')
        }
        // Server lost our membership on this socket. Re-claim the saved
        // name. Do not clear joined -- that brings the name field back.
        if (msg.reason === 'join chat first') {
          const n = (nameRef.current || '').trim()
          const wsNow = wsRef?.current
          const now = Date.now()
          if (n && wsNow && wsNow.readyState === WebSocket.OPEN && now - rejoinAtRef.current > 1500) {
            rejoinAtRef.current = now
            wsNow.send(joinMsg(n))
          }
        }
      }
    }
    ws.addEventListener('message', handler)
    return () => ws.removeEventListener('message', handler)
  }, [wsRef?.current, status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Claim a name. Persists it and sends chat_join; server replies
  // chat_joined (flips `joined`) or chat_error (e.g. name taken).
  const join = useCallback((rawName) => {
    const n = (rawName ?? name).trim()
    if (!n) return
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    // Already joined under this exact name (e.g. the auto-claim on
    // activation already took it) — re-sending chat_join makes the server
    // reject a duplicate of your OWN live connection as "name taken".
    // Treat it as a satisfied no-op; only persist.
    if (joined && n === name) {
      setError('')
      localStorage.setItem(NAME_STORAGE_KEY, n)
      return
    }
    localStorage.setItem(NAME_STORAGE_KEY, n)
    setName(n)
    nameRef.current = n
    setError('')
    ws.send(joinMsg(n))
    // Persist to the authed email identity so it follows the login. Fire-
    // and-forget; server validates format + uniqueness and may 409. If the
    // WS join is accepted but this 409s, the names diverged — surface it.
    fetch('/api/display-name', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: n }),
    })
      .then(r => r.json().catch(() => null))
      .then(d => { if (d?.chat_key) adoptKey(d.chat_key); if (d && d.error) setError(d.error) })
      .catch(() => { /* offline / not authed — local + WS join still stand */ })
  }, [name, wsRef])

  // Send the current input. Server stamps id/name/ts and the
  // broadcast echo (sender included) appends it to messages.
  // send(text?) - optional string overrides the composer (AI tab passes '@grok ...').
  // onClick passes an event, so only a string counts as an override.
  const send = useCallback((over) => {
    const text = (typeof over === 'string' ? over : input).trim()
    if (!text) return
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    // @grok stays in this chat for this user. It is not a lobby message.
    if (/^@grok\b/i.test(text)) {
      const question = text.replace(/^@grok\b[:\s,]*/i, '').trim()
      if (!question) return
      let context = {}
      try { context = getChartContextRef?.current?.() || {} } catch { context = {} }
      const safe = {
        token: String(context.token || '').slice(0, 80),
        symbol: String(context.symbol || '').slice(0, 32),
        timeframe: context.timeframe ?? null,
        price: typeof context.price === 'number' ? context.price : null,
        note: String(context.note || '').slice(0, 240),
        // Settings + panels the assistant may change / spotlight (registry only).
        settings: (() => { try { return listSettings().map(s => ({ ...s, options: s.options ? s.options.map(o => o.value) : undefined })) } catch { return [] } })(),
        panels: (() => { try { return listPanels() } catch { return [] } })(),
        // Active chart: tf, visible range, last ~60 candles, swings, drawings.
        chart: chartContext(),
        // Open-ended app actions Grok may call via app_action (utils/grokRegistry).
        actions: (() => { try { return listGrokActions() } catch { return [] } })(),
      }
      const ts = Date.now()
      setMessages(prev => {
        const next = [...prev, {
          id: `gl_${ts}`,
          name: nameRef.current || 'you',
          text,
          ts,
          private: true,
        }]
        return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next
      })
      ws.send(JSON.stringify({ action: 'grok_ask', text, context: safe }))
      setInput('')
      return
    }
    ws.send(JSON.stringify({ action: 'chat_send', text }))
    setInput('')
  }, [input, wsRef, getChartContextRef])
  // grok-orders-20261021: the BUY picker's "+ New order" asks Grok through
  // this send path. Two chat hooks are mounted; the first one owns the event.
  const sendRef = useRef(send)
  sendRef.current = send
  useEffect(() => {
    const me = {}
    if (!window.__ctGrokAskOwner) window.__ctGrokAskOwner = me
    const h = (e) => {
      try {
        if (window.__ctGrokAskOwner !== me) return
        const t = String(e.detail?.text || '').trim()
        if (t) sendRef.current('@grok ' + t)
      } catch {}
    }
    window.addEventListener('solcheddar-grok-ask', h)
    return () => { window.removeEventListener('solcheddar-grok-ask', h); if (window.__ctGrokAskOwner === me) window.__ctGrokAskOwner = null }
  }, [])

  // react(id, emoji, isPrivate) - toggle your reaction on a message.
  // Applied locally right away; for lobby messages the server's chat_react
  // broadcast then replaces the map with the shared one. Private (@grok)
  // messages never reach the server, so theirs stay local to this session.
  const react = useCallback((id, emoji, isPrivate) => {
    const who = (nameRef.current || '').trim()
    if (!who || id == null || !emoji) return
    setMessages(prev => prev.map(m => (m.id === id ? toggleReaction(m, emoji, who) : m)))
    if (isPrivate) return
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try { ws.send(JSON.stringify({ action: 'chat_react', id, emoji })) } catch {}
  }, [wsRef])

  // Membership is per socket, so a reconnect must re-send chat_join.
  // Joined is NOT cleared on the way: the composer stays up and the
  // saved display name is claimed again with no prompt.
  // Name priority:
  //   1. GET /api/display-name for the authed email. If it returns a
  //      name, lock joined immediately and join with it.
  //   2. localStorage name, and cheddarChatJoined so a refresh skips
  //      the name field.
  const identityFetchedRef = useRef(false)
  useEffect(() => {
    if (!active) return
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    if (!historyRequestedRef.current) {
      historyRequestedRef.current = true
      ws.send(JSON.stringify({ action: 'chat_history' }))
    }

    const sendJoin = (n) => {
      const nameToJoin = (n || '').trim()
      if (!nameToJoin) return
      const ws2 = wsRef?.current
      if (!ws2 || ws2.readyState !== WebSocket.OPEN) return
      rejoinAtRef.current = Date.now()
      ws2.send(joinMsg(nameToJoin))
    }

    if (!identityFetchedRef.current) {
      identityFetchedRef.current = true
      fetch('/api/display-name', { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          adoptKey(d?.chat_key)   // before any join below
          const serverName = (d && typeof d.name === 'string') ? d.name.trim() : ''
          if (serverName) {
            try {
              localStorage.setItem(NAME_STORAGE_KEY, serverName)
              localStorage.setItem(JOINED_STORAGE_KEY, '1')
            } catch {}
            nameRef.current = serverName
            setName(serverName)
            setJoined(true)
            setError('')
            sendJoin(serverName)
          } else {
            const local = (nameRef.current || '').trim()
            let kept = false
            try { kept = localStorage.getItem(JOINED_STORAGE_KEY) === '1' && !!local } catch {}
            if (kept) {
              setJoined(true)
              sendJoin(local)
            } else if (local) {
              sendJoin(local)
            }
          }
        })
        .catch(() => {
          const local = (nameRef.current || '').trim()
          if (local) sendJoin(local)
        })
        .finally(() => setIdentityReady(true))
      return
    }

    // Socket is open again. Previous accept was the old connection.
    // Re-claim the saved name. Leave joined set so the name field stays hidden.
    ackedRef.current = false
    sendJoin(nameRef.current)
  }, [active, status]) // eslint-disable-line react-hooks/exhaustive-deps

  // Pin scroll to the newest message while visible.
  useEffect(() => {
    if (active && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight
    }
  }, [messages, active])

  return {
    messages,        // chat history + live
    name, setName,   // controlled name field (only before a name is saved)
    joined,          // true from cheddarChatJoined or from GET /api/display-name
    identityReady,   // false only while the first GET /api/display-name is in flight
    input, setInput, // controlled message field
    error,           // last chat_error reason ('' when clear)
    join,            // join(name?) — claim/persist name
    send,            // send() — send current input
    react,           // react(id, emoji, isPrivate) — toggle your reaction
    bodyRef,         // ref for the scrollable message list
  }
}